///
/// EarthBrowser
///
/// Created by Matt Giger on 1/7/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type geographic } from "@/earthbrowser/utils/geographic"
import { trig } from "@stovokor/math/math"
import { WebMercatorTileId } from "./WebMercatorTileId"

export class WebMercatorTileHitCounter {
	constructor(
		public readonly id: WebMercatorTileId,
		public count: number = 1,
		public centerDistance: number = 0
	) {}
}

export class WebMercatorTileLevel {
	public readonly level: number
	public readonly rows: number
	public readonly cols: number
	public bins = new Map<string, WebMercatorTileHitCounter>()

	constructor(level: number) {
		this.level = level
		this.rows = 1 << level
		this.cols = 1 << level
	}

	public clear(): void {
		this.bins.clear()
	}

	public addHit(geo: geographic, centerGeo: geographic | null): void {
		const tileId = WebMercatorTileId.fromLonLatRad(this.level, geo.longitude, geo.latitude)
		const bin = this.bins.get(tileId.key)
		if (bin) {
			bin.count++
			return
		}

		let centerDistance = 0
		if (centerGeo) {
			// Approx: distance in radians (good enough for priority ordering)
			// Use tile center in lon/lat.
			const n = 1 << this.level
			const u0 = (tileId.x + 0.5) / n
			const v0 = (tileId.y + 0.5) / n
			const tileCenterLon = u0 * trig.two_pi - trig.pi

			// inverse mercator:
			// lat = 2*atan(exp((0.5 - v)*2π)) - π/2
			const tileCenterLat = 2 * Math.atan(Math.exp((0.5 - v0) * trig.two_pi)) - trig.half_pi

			const dLat = centerGeo.latitude - tileCenterLat
			const dLon = centerGeo.longitude - tileCenterLon
			centerDistance = Math.sqrt(dLat * dLat + dLon * dLon)
		}

		this.bins.set(tileId.key, new WebMercatorTileHitCounter(tileId, 1, centerDistance))
	}
}


